import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { keys } from "@/lib/i18n/portal";
import { STORE_SLUG_TAKEN } from "@/lib/routing/store-slug";

type Translate = (key: string) => string;

export type CreateErrorField = "phone" | "email" | "storeSlug";

/**
 * The admin-facing message for a refused seller/business create, by the CODE
 * the API returns — never its English `message` shown as is on an Arabic page.
 *
 * - 409 `DUPLICATE_RECORD` is the seller's unique phone or email. The code does
 *   not say which, so the server's own field word picks the sentence; with
 *   neither word the sentence names both.
 * - 409 `STORE_SLUG_TAKEN` is a reserved or already-used store link.
 * - A 400 that names `storeSlug` is the link pattern (the client checks it
 *   first, so this is the server agreeing).
 *
 * `field` is where the message belongs, so the form can put it under the
 * control the admin has to change rather than in a banner above the form.
 */
export function describeCreateError(
  error: unknown,
  t: Translate,
): { field: CreateErrorField | null; message: string } {
  if (!(error instanceof ApiJsonClientError) || error.status >= 500) {
    return { field: null, message: t(keys.errors.generic) };
  }
  const serverText = String(error.message ?? "").toLowerCase();
  if (error.code === "DUPLICATE_RECORD") {
    if (serverText.includes("phone"))
      return { field: "phone", message: t(keys.admin.sellerPhoneTaken) };
    if (serverText.includes("email"))
      return { field: "email", message: t(keys.admin.sellerEmailTaken) };
    return { field: null, message: t(keys.admin.sellerDuplicate) };
  }
  if (error.code === STORE_SLUG_TAKEN) {
    return { field: "storeSlug", message: t(keys.admin.storeSlugTaken) };
  }
  if (error.status === 400 && serverText.includes("storeslug")) {
    return { field: "storeSlug", message: t(keys.admin.storeSlugInvalid) };
  }
  return { field: null, message: t(keys.errors.generic) };
}
